import { chartGridRows } from '../ui/DashboardPresetGrid/chartLayout';
import type { PresetChartFormValues } from './dashboardPresetForm';

/**
 * Место, куда встаёт перетаскиваемая диаграмма, — в понятиях состава: порядок и начало строки.
 *
 * - `before` — перед диаграммой `chartId` в её строке; перед первой диаграммой строки перетаскиваемая
 *   открывает эту строку собой, а прежняя первая идёт следом за ней;
 * - `after` — следом за диаграммой `chartId` в её строке;
 * - `newRow` — новой строкой сразу за строкой, последняя диаграмма которой — `afterChartId`; строка,
 *   стоявшая ниже, остаётся отдельной строкой.
 */
export type ChartDropPlace =
  | { kind: 'before'; chartId: string }
  | { kind: 'after'; chartId: string }
  | { kind: 'newRow'; afterChartId: string };

/**
 * Состав дашборда с диаграммой `sourceId`, перенесённой на место `place`.
 *
 * Место отсчитывается от раскладки, какой её видел пользователь: первая диаграмма строки узнаётся по
 * составу до переноса. Признак начала строки получает только то, что строку открывает, — первой
 * диаграмме состава он не нужен: строку она открывает и без него, а записанный признак делал бы
 * дашборд правленым там, где ничего не изменилось.
 */
export const applyChartDrop = (
  charts: PresetChartFormValues[],
  sourceId: string,
  place: ChartDropPlace,
): PresetChartFormValues[] => {
  const sourceIndex = charts.findIndex((chart) => chart.chartId === sourceId);
  if (sourceIndex < 0) return charts;
  const source = charts[sourceIndex];

  const rest = charts.filter((chart) => chart.chartId !== sourceId);
  const anchorId = place.kind === 'newRow' ? place.afterChartId : place.chartId;
  const anchorIndex = rest.findIndex((chart) => chart.chartId === anchorId);
  if (anchorIndex < 0) return charts;

  const rowHeads = new Set(chartGridRows(charts).map((row) => charts[row.chartIndexes[0]].chartId));
  const isSourceRowHead = rowHeads.has(sourceId);
  const previousId = charts[sourceIndex - 1]?.chartId;
  const nextId = charts[sourceIndex + 1]?.chartId;

  const placed = (index: number, startsRow: boolean): PresetChartFormValues[] => [
    ...rest.slice(0, index),
    { ...source, startsRow: startsRow && index > 0 },
    ...rest.slice(index),
  ];

  switch (place.kind) {
    case 'before': {
      if (!rowHeads.has(anchorId)) {
        // Диаграмма и так стоит перед соседкой своей строки — место то же, что было
        return nextId === anchorId ? charts : placed(anchorIndex, false);
      }
      // Прежняя первая диаграмма строки идёт следом за перенесённой, а не открывает строку сама
      const reordered = placed(anchorIndex, true);
      reordered[anchorIndex + 1] = { ...reordered[anchorIndex + 1], startsRow: false };
      return reordered;
    }
    case 'after':
      return placed(anchorIndex + 1, false);
    case 'newRow': {
      // Диаграмма и так открывает строку сразу за этой
      if (previousId === anchorId && isSourceRowHead) return charts;
      const reordered = placed(anchorIndex + 1, true);
      // Строка ниже не подтягивается к новой: место показано отдельной строкой между ними
      const nextIndex = anchorIndex + 2;
      if (nextIndex < reordered.length) {
        reordered[nextIndex] = { ...reordered[nextIndex], startsRow: true };
      }
      return reordered;
    }
  }
};
